import { canEditVariantVotes } from "./variant-review";
import { mentionsIn } from "./mentions";
import type { RecordAuthorizers } from "./sync/room";
import { createCommentReactionId, type TLRecord } from "@tldraw/tlschema";
import { authorizeNativeDocument } from "./document-session";

export const ROLES = ["owner", "editor", "commenter", "viewer"] as const;
export type Role = (typeof ROLES)[number];
export type SessionMeta = { user: { id: string; name: string }; role: Role };

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && ROLES.some((role) => role === value);
}
export function canEdit(role: Role) {
  return role === "owner" || role === "editor";
}
export function canComment(role: Role) {
  return canEdit(role) || role === "commenter";
}

export const commentObjectTypes = [
  "comment",
  "comment-thread",
  "comment-reaction",
];

// Transport-independent rules shared by Node and the future Worker sync room.
// The transport must also enforce isReadonly/objectAccess from the member role.
export const recordAuthorizers: RecordAuthorizers<TLRecord, SessionMeta> = {
  shape: ({ type, next, prev, session }) => {
    if (
      type === "update" &&
      !canEditVariantVotes(prev.meta, next.meta, session.meta.user)
    ) return null;
    if (type === "create")
      return authorizeNativeDocument(null, next, session.sessionId, canEdit(session.meta.role));
    if (type === "delete")
      return authorizeNativeDocument(prev, null, session.sessionId, canEdit(session.meta.role));
    return authorizeNativeDocument(prev, next, session.sessionId, canEdit(session.meta.role));
  },
  comment: ({ type, prev, next, session }) => {
    if (type === "create")
      return {
        ...next,
        authorId: session.meta.user.id,
        meta: { ...next.meta, author: session.meta.user.name },
      };
    if (type === "delete") return null;
    // A removed comment remains in the object store and can be restored by its
    // author. Editing must never rewrite its immutable identity or review event.
    if (
      prev.authorId !== session.meta.user.id ||
      prev.authorId !== next.authorId ||
      prev.threadId !== next.threadId ||
      prev.pageId !== next.pageId ||
      prev.createdAt !== next.createdAt ||
      JSON.stringify((prev.meta.review as any)?.action ?? null) !== JSON.stringify((next.meta.review as any)?.action ?? null)
    ) return null;
    const derived=[...new Set(mentionsIn(next.body).map(m=>m.userId))];
    const reviewChanged=JSON.stringify(prev.meta.review??null)!==JSON.stringify(next.meta.review??null);
    if(reviewChanged && JSON.stringify((next.meta.review as any)?.mentions ?? [])!==JSON.stringify(derived))return null;
    return { ...next, meta: { ...prev.meta, ...(reviewChanged?{review:{...((prev.meta.review??{}) as object),mentions:derived}}:{}), author: session.meta.user.name } };
  },
  "comment-thread": ({ type, next, prev, session }) => {
    if (type === "create") return { ...next, createdBy: session.meta.user.id };
    if (type === "delete") return null;
    if (prev.createdBy !== next.createdBy || prev.pageId !== next.pageId || prev.createdAt !== next.createdAt)
      return null;
    // Everyone who can comment may resolve a discussion. Only its creator may
    // change the anchor or review-mark data attached to the original target.
    if (prev.createdBy !== session.meta.user.id &&
      (JSON.stringify(prev.anchor) !== JSON.stringify(next.anchor) ||
       JSON.stringify(prev.meta) !== JSON.stringify(next.meta) ||
       prev.isDeleted !== next.isDeleted)) return null;
    return next;
  },
  "comment-reaction": ({ type, prev, next, session }) => {
    if (type === "delete")
      return prev.userId === session.meta.user.id ? prev : null;
    if (
      next.userId !== session.meta.user.id ||
      next.id !==
        createCommentReactionId(next.commentId, next.userId, next.emoji)
    )
      return null;
    if (
      type === "update" &&
      (prev.userId !== next.userId ||
        prev.commentId !== next.commentId ||
        prev.threadId !== next.threadId ||
        prev.pageId !== next.pageId ||
        prev.emoji !== next.emoji)
    )
      return null;
    return next;
  },
};
